/**
 * RestoID — Identifier-first register landing (`first_screen=identifier:register`).
 * ---------------------------------------------------------------------------
 * Custom RestoID PRESENTATION on the PRISTINE upstream LOGIC of
 * `src/pages/IdentifierRegister` + `src/components/IdentifierRegisterForm`:
 *   - which identifiers to ask for .. upstream `useIdentifierSignUpMethods`
 *     (URL `identifier` params intersected with the SIE sign-up config)
 *   - the sign-up-disabled guard .... upstream `useSieMethods().signInMode`
 *   - submit / flow routing ......... upstream `useOnSubmit` (username registers
 *     directly, an e-mail is checked against the SSO connectors first, anything
 *     else sends a verification code)
 *   - username policy ............... upstream `useUsernamePolicyDescription` +
 *     `validateIdentifierField(..., usernamePolicy)` + `isUsernamePolicyViolation`
 *   - SSO type-ahead ................ upstream `useSingleSignOnWatch`
 *   - terms gate .................... upstream `useTerms` + `TermsAndPrivacyCheckbox`
 *   - captcha ....................... upstream `CaptchaBox`
 *
 * Deliberate differences from upstream, and why:
 *  - `FocusedAuthPageLayout` is replaced by `FirstScreenLayout` + `ScreenHead`
 *    (title + the upstream description as the subtitle) and its `authOptionsLink`
 *    by a centred `LinkText`, the same shape `custom/pages/SingleSignOnLanding`
 *    uses. A register landing keeps a TEXT title rather than `custom/pages/SignIn`'s
 *    `BrandHead`, because a bare logo would not say whether you are signing in or
 *    creating an account — this is also what the design source's register screen does.
 *  - upstream's `SmartInputField` becomes ONE explicit RestoID control, phone-first
 *    — shared with `custom/pages/IdentifierSignIn` (`IdentifierField`), so both
 *    landings ask for the identifier the same way.
 *  - the layout's policy-gated terms FOOTER becomes the RestoID terms sentence
 *    (`custom/components/Terms`), gated by exactly the `footerTermsDisplayPolicies`
 *    upstream passed here — Automatic only.
 *  - upstream's `required` validation branch is unreachable because the CTA is
 *    disabled until the field is filled; every `invalid` branch (including the
 *    username-policy sentence) is kept verbatim.
 *  - NOTE the terms gate runs BEFORE the SSO hand-off here, and for every policy
 *    except Automatic — that asymmetry with the sign-in landing is upstream's, not
 *    ours, and is reproduced as-is.
 *  - upstream's `useEffect(() => { if (!isValid) clearErrorMessage(); })` has no
 *    equivalent (there is no react-hook-form `isValid`); the server error is
 *    cleared at the top of every submit, as `custom/pages/SignInPassword` does.
 *  - the `description.all_account_creation_options` link still points at
 *    `/${experience.routes.register}`, exactly as upstream. On RestoID that route
 *    is `custom/pages/Register`, which redirects to `/sign-in` — registration is
 *    hidden and accounts are created on first sign-in. Keeping upstream's target
 *    means the link keeps working the day registration is un-hidden.
 * Swapped in for `./pages/IdentifierRegister` via `src/App.tsx`.
 */
import { AgreeToTermsPolicy, SignInIdentifier, SignInMode, experience } from '@logto/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useOnSubmit from '@/components/IdentifierRegisterForm/use-on-submit';
import CaptchaBox from '@/containers/CaptchaBox';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods, useUsernamePolicyDescription } from '@/hooks/use-sie';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import useIdentifierSignUpMethods from '@/pages/IdentifierRegister/use-identifier-sign-up-methods';
import { isUsernamePolicyViolation } from '@/shared/utils/validate-username';
import { identifierInputDescriptionMap, validateIdentifierField } from '@/utils/form';

import { Terms } from '../components/Terms';
import { Button, C, FONT, LinkText, ScreenHead, Stack } from '../components/restoid';

import { IdentifierField, useIdentifierValue } from './IdentifierSignIn';

type FormProps = {
  identifier: SignInIdentifier;
  signUpMethods: SignInIdentifier[];
  /** Upstream's `footerTermsDisplayPolicies` verdict for this policy. */
  showTerms: boolean;
};

const IdentifierRegisterForm = ({ identifier, signUpMethods, showTerms }: FormProps) => {
  const { t } = useTranslation();
  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { experienceSettings } = useContext(PageContext);
  const usernamePolicyDescription = useUsernamePolicyDescription();
  const { value, setValue, isFilled, submitValue } = useIdentifierValue(identifier, signUpMethods);
  const [invalidMessage, setInvalidMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  // Upstream watched the react-hook-form value; memoize so the debounced SSO
  // lookup is not restarted on every render.
  const identifierInput = useMemo(
    () => ({ type: identifier, value: submitValue }),
    [identifier, submitValue]
  );
  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(identifierInput);

  const actionLabel = showSingleSignOnForm
    ? t('action.single_sign_on')
    : t('action.create_account');

  const submit = useCallback(async () => {
    if (!isFilled || submitting) {
      return;
    }
    clearErrorMessage();
    setInvalidMessage(undefined);

    const validationError = validateIdentifierField(
      identifier,
      submitValue,
      experienceSettings?.usernamePolicy
    );

    if (validationError) {
      /**
       * This page shows no upfront policy description (the identifier may be of any
       * enabled type), so a policy violation surfaces the full requirements sentence
       * instead of the specific violation. Hard-floor violations stay specific — the
       * requirements sentence does not describe them.
       */
      if (usernamePolicyDescription && isUsernamePolicyViolation(validationError)) {
        setInvalidMessage(usernamePolicyDescription);
        return;
      }

      setInvalidMessage(
        typeof validationError === 'string'
          ? t(`error.${validationError}`)
          : t(`error.${validationError.code}`, validationError.data ?? {})
      );
      return;
    }

    setIdentifierInputValue({ type: identifier, value: submitValue });

    // Upstream order: terms first (for every policy but Automatic), then SSO.
    if (
      agreeToTermsPolicy &&
      agreeToTermsPolicy !== AgreeToTermsPolicy.Automatic &&
      !(await termsValidation())
    ) {
      return;
    }

    if (showSingleSignOnForm) {
      await navigateToSingleSignOn();
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(identifier, submitValue);
    } finally {
      setSubmitting(false);
    }
  }, [
    isFilled,
    submitting,
    clearErrorMessage,
    identifier,
    submitValue,
    experienceSettings?.usernamePolicy,
    usernamePolicyDescription,
    t,
    setIdentifierInputValue,
    agreeToTermsPolicy,
    termsValidation,
    showSingleSignOnForm,
    navigateToSingleSignOn,
    onSubmit,
  ]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Stack gap={20}>
        <IdentifierField
          autoComplete="off"
          identifier={identifier}
          value={value}
          errorMessage={invalidMessage ?? errorMessage}
          onChange={(next) => {
            setInvalidMessage(undefined);
            setValue(next);
          }}
        />
        {showSingleSignOnForm ? (
          <div style={{ textAlign: 'center', color: C.sub, font: `400 13px/1.5 ${FONT}` }}>
            {t('description.single_sign_on_enabled')}
          </div>
        ) : null}
        {/* Upstream hides the checkbox only for the Automatic policy. */}
        {agreeToTermsPolicy === AgreeToTermsPolicy.Automatic ? null : <TermsAndPrivacyCheckbox />}
        <CaptchaBox />
        {/* No onClick: an onClick here would run the submit a second time per click. */}
        <Button
          htmlType="submit"
          title={actionLabel}
          isLoading={submitting}
          isDisabled={!isFilled}
        />
        {showTerms ? <Terms action={actionLabel} /> : null}
      </Stack>
    </form>
  );
};

const IdentifierRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithPreservedSearchParams();
  const { agreeToTermsPolicy } = useTerms();
  const signUpMethods = useIdentifierSignUpMethods();
  const { signInMode } = useSieMethods();

  // Prefer phone when offered; otherwise take whatever the tenant put first
  // (same rule as `custom/pages/SignIn` and the sign-in landing).
  const identifier = useMemo(
    () => signUpMethods.find((item) => item === SignInIdentifier.Phone) ?? signUpMethods[0],
    [signUpMethods]
  );

  /**
   * Fallback to sign-in page in the following cases:
   * - Sign-in mode is set to `SignIn` (user registration is not enabled in the sign-in experience configuration)
   * - No sign up methods are available
   */
  if (signInMode === SignInMode.SignIn || signUpMethods.length === 0 || !identifier) {
    return <Navigate to={`/${experience.routes.signIn}`} />;
  }

  // Upstream's `footerTermsDisplayPolicies` for this page.
  const showTerms = agreeToTermsPolicy === AgreeToTermsPolicy.Automatic;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.create_your_account' }}>
      <SingleSignOnFormModeContextProvider>
        <Stack gap={22}>
          <ScreenHead
            title={t('description.create_account')}
            sub={t('description.identifier_register_description', {
              types: signUpMethods.map((item) => t(identifierInputDescriptionMap[item])),
            })}
          />
          <IdentifierRegisterForm
            identifier={identifier}
            signUpMethods={signUpMethods}
            showTerms={showTerms}
          />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LinkText onClick={() => navigate(`/${experience.routes.register}`)}>
              {t('description.all_account_creation_options')}
            </LinkText>
          </div>
        </Stack>
      </SingleSignOnFormModeContextProvider>
    </FirstScreenLayout>
  );
};

export default IdentifierRegister;
